import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Session timeout policy
 * ------------------------------------------------------------------
 * The Y.O. admin shell hosts test-admin (sandbox) sessions minted via
 * /admin/test-login. Those sessions need stricter lifecycle rules than a
 * normal seat-belted admin login because the bootstrap key is shared with
 * preview testers and the credentials live entirely in the browser.
 *
 * Two independent caps are enforced:
 *   1. INACTIVITY cap   — auto-logout if the browser sees no user input
 *      for `idleMs`. Resets on mouse / touch / key / scroll / focus events.
 *   2. ABSOLUTE cap     — auto-logout no later than `absoluteMs` after the
 *      session was first observed, regardless of activity. This stops a
 *      tester walking away from an active tab from leaving an open seat.
 *
 * `warnBeforeMs` controls how many milliseconds before the *earlier* of the
 * two caps the consumer should surface a "you're about to be signed out"
 * confirmation dialog. The countdown is exposed as `secondsRemaining` and
 * the host UI is expected to render it prominently.
 *
 * The hook is event-source agnostic — it never touches Supabase. The host
 * (AdminAuth) wires `onTimeout` to the actual `signOut()` call and to any
 * audit logging.
 */
export type TimeoutReason = 'inactivity' | 'absolute';

export interface SessionTimeoutOptions {
  /** Whether the timeout machinery is active. Pass false to disable. */
  enabled: boolean;
  /** Maximum idle time (ms) before forced logout. */
  idleMs: number;
  /** Hard cap (ms) from session start, regardless of activity. */
  absoluteMs: number;
  /** How many ms before expiry to surface a warning to the user. */
  warnBeforeMs: number;
  /** Anchor for the absolute cap. Defaults to "now" on first activation. */
  sessionStart?: number;
  /** Called once when expiry is reached. */
  onTimeout: (reason: TimeoutReason) => void;
}

export interface SessionTimeoutState {
  /** Seconds until forced logout (0 once expired). */
  secondsRemaining: number;
  /** True while the warning window is active (countdown <= warnBeforeMs). */
  isWarning: boolean;
  /** Which cap is currently going to fire first. */
  activeReason: TimeoutReason;
  /** Reset the inactivity timer (e.g., when the user clicks "Stay signed in"). */
  extendSession: () => void;
  /** Convenience: ms since the last recorded activity event. */
  msSinceLastActivity: number;
}

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
];

/**
 * Throttle a handler so high-frequency events (mousemove, scroll) only
 * write our last-activity timestamp at most once every `windowMs`.
 */
const throttle = (fn: () => void, windowMs: number) => {
  let last = 0;
  return () => {
    const now = Date.now();
    if (now - last < windowMs) return;
    last = now;
    fn();
  };
};

export const useSessionTimeout = ({
  enabled,
  idleMs,
  absoluteMs,
  warnBeforeMs,
  sessionStart,
  onTimeout,
}: SessionTimeoutOptions): SessionTimeoutState => {
  // The session start is captured once and then frozen. Re-rendering with a
  // different `sessionStart` value is intentional (e.g., a fresh sign-in)
  // and resets the absolute cap.
  const startRef = useRef<number>(sessionStart ?? Date.now());
  useEffect(() => {
    if (sessionStart && sessionStart !== startRef.current) {
      startRef.current = sessionStart;
    }
  }, [sessionStart]);

  const [lastActivity, setLastActivity] = useState<number>(() => Date.now());
  const [now, setNow] = useState<number>(() => Date.now());
  const firedRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  // Activity listeners: throttled so we don't thrash React state on every
  // mousemove. Visibility/focus changes also count as activity so a tester
  // returning to the tab gets a fair grace window.
  useEffect(() => {
    if (!enabled) return;
    const bump = throttle(() => setLastActivity(Date.now()), 1000);
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, bump, { passive: true }),
    );
    const onVisibility = () => { if (!document.hidden) bump(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', bump);
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, bump));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', bump);
    };
  }, [enabled]);

  // Tick the clock once a second so the UI countdown stays live.
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [enabled]);

  // Reset the "fired" guard whenever the host re-enables us with a fresh
  // session start (e.g., user logged out and a new session was minted).
  useEffect(() => {
    if (enabled) firedRef.current = false;
  }, [enabled, sessionStart]);

  const idleDeadline = lastActivity + idleMs;
  const absoluteDeadline = startRef.current + absoluteMs;
  const activeReason: TimeoutReason = idleDeadline <= absoluteDeadline ? 'inactivity' : 'absolute';
  const expiresAt = Math.min(idleDeadline, absoluteDeadline);
  const msRemaining = Math.max(0, expiresAt - now);
  const secondsRemaining = Math.ceil(msRemaining / 1000);

  // Fire onTimeout exactly once per session, defensively guarded so re-renders
  // can't cause a sign-out storm.
  useEffect(() => {
    if (!enabled) return;
    if (msRemaining > 0) return;
    if (firedRef.current) return;
    firedRef.current = true;
    onTimeoutRef.current(activeReason);
  }, [enabled, msRemaining, activeReason]);

  const extendSession = useCallback(() => setLastActivity(Date.now()), []);

  return useMemo<SessionTimeoutState>(() => ({
    secondsRemaining,
    isWarning: enabled && msRemaining > 0 && msRemaining <= warnBeforeMs,
    activeReason,
    extendSession,
    msSinceLastActivity: Math.max(0, now - lastActivity),
  }), [enabled, secondsRemaining, msRemaining, warnBeforeMs, activeReason, extendSession, now, lastActivity]);
};

/**
 * Format a seconds count as `m:ss` (e.g. 65 → "1:05") for the countdown chip.
 */
export const formatTimeoutCountdown = (seconds: number): string => {
  const safe = Math.max(0, Math.floor(seconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};